import { toFaDigits } from '../helpers/digits'

// The phone's own calendar events, in the terms the app shows them in.
//
// Pure: no plugin, no storage, no DOM. The service that fetches them is in
// services/phone-calendar.ts. Everything here is date arithmetic, which is the
// part the two platforms disagree about — and so the part worth testing.

// One event, once it is ours.
export interface IPhoneEvent {
    id: string
    title: string
    allDay: boolean
    start: number // epoch milliseconds
    end: number // epoch milliseconds
    calendar: string // the calendar it lives in; '' where the platform omits it
    // Set only when the row is one of Emrouz's own events rather than the
    // phone's: the id in the event store, which is what makes the row
    // editable in place. The plugin never fills this in.
    eventId?: string
}

// Whether a row is one of the phone's events rather than one of Emrouz's own.
export const fromPhone = (e: IPhoneEvent) => !e.eventId

// One event as the plugin hands it over. Declared here rather than imported
// from the package: that package belongs to the native shell, and this bundle
// also has to build for the web, where it is not installed.
export interface IRawEvent {
    id: string
    title?: string
    startDate: number
    endDate: number
    isAllDay?: boolean
    calendarId?: string
    calendarName?: string
}

const HALF_DAY = 12 * 60 * 60 * 1000

// An event saved with no title. The phone's own calendar writes something in
// its place too, so the day page cannot leave the row blank.
export const UNTITLED = 'بدون عنوان'

// What an all-day row says where the others give an hour.
export const ALL_DAY = 'تمام‌روز'

// A span longer than this is a malformed event, not a real one, and walking it
// day by day would cost more than it is worth. The same year-and-a-bit horizon
// the reminder scheduler and the widget work to.
const MAX_SPAN = 400

export function toEvent(raw: IRawEvent): IPhoneEvent {
    return {
        id: raw.id,
        title: (raw.title || '').trim() || UNTITLED,
        allDay: !!raw.isAllDay,
        start: raw.startDate,
        end: raw.endDate,
        calendar: raw.calendarName || '',
    }
}

// The day key the rest of the app already uses for a Gregorian date — the shape
// the /day?d= query takes and the widget's table is keyed by.
export const dayKey = (gy: number, gm: number, gd: number) => `${gy}-${gm}-${gd}`

const midnightAt = (ts: number) => {
    const d = new Date(ts)
    return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

const keyOf = (d: Date) => dayKey(d.getFullYear(), d.getMonth() + 1, d.getDate())

// The days an event covers, as day keys.
//
// Two platform habits have to survive this, and both are about midnight.
//
// A timed event's end is exclusive: one running to 00:00 belongs to the day
// before, not to the day it touches for an instant. Backing a millisecond off
// the end says that, and says nothing about any other event.
//
// An all-day event is stored at midnight, but the platforms disagree about
// whose. Android's CalendarContract writes UTC midnight; iOS's EventKit writes
// local midnight, and puts the end on the last day at 23:59:59 rather than on
// the next day at 00:00. Reading a local date straight off any of those gets
// the day wrong somewhere. So neither end is read at midnight: the start is
// pushed twelve hours on and the end twelve hours back, which lands both in the
// middle of the day they actually mean under every one of those conventions.
export function daysOf(e: IPhoneEvent): string[] {
    const first = midnightAt(e.allDay ? e.start + HALF_DAY : e.start)
    const last = midnightAt(e.allDay ? e.end - HALF_DAY : Math.max(e.end - 1, e.start))

    const out: string[] = []
    // Day by day through the Date constructor rather than by adding 24 hours,
    // so a daylight-saving change inside the span does not shift the walk.
    for (let i = 0; i < MAX_SPAN; i++) {
        const day = new Date(first.getFullYear(), first.getMonth(), first.getDate() + i)
        if (day.getTime() > last.getTime()) break
        out.push(keyOf(day))
    }
    // A start after the end leaves the walk empty; the day it starts on is
    // still the honest answer.
    return out.length ? out : [keyOf(first)]
}

// All-day first — they are the day's backdrop rather than a point inside it —
// then by the hour they start, and by title where two start together, so a list
// does not reshuffle itself between two fetches of the same day.
const order = (a: IPhoneEvent, b: IPhoneEvent) =>
    Number(b.allDay) - Number(a.allDay) || a.start - b.start || a.title.localeCompare(b.title)

export const sortEvents = (list: IPhoneEvent[]): IPhoneEvent[] => [...list].sort(order)

// Events by day key, each day's list in reading order. A multi-day event
// appears under every day it covers.
export function indexByDay(events: IPhoneEvent[]): Record<string, IPhoneEvent[]> {
    const out: Record<string, IPhoneEvent[]> = {}
    for (const e of events) {
        for (const key of daysOf(e)) {
            if (!out[key]) out[key] = []
            out[key].push(e)
        }
    }
    for (const key in out) out[key] = sortEvents(out[key])
    return out
}

const clock = (ts: number) => {
    const d = new Date(ts)
    return toFaDigits(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`)
}

// What a row writes beside the title.
export function eventTime(e: IPhoneEvent): string {
    if (e.allDay) return ALL_DAY
    const last = Math.max(e.end - 1, e.start)
    // A span crossing midnight would read as a range inside a single day on
    // every day it covers, so it gives the hour it starts and leaves it there.
    if (keyOf(new Date(e.start)) !== keyOf(new Date(last))) return clock(e.start)
    return `${clock(e.start)} تا ${clock(e.end)}`
}
